/**
 * ExtrasPanel — independent, manually-built extras for a job (Oct-13 list #2).
 *
 * An extra (e.g. "CSI-070") lives entirely inside the job's takeoff document
 * (doc.extras) — no label or tagged takeoff required. Each extra holds its own
 * ordered groups of line items; items are manual entries or COPIES of estimate
 * lines (never linked — edits inside an extra never touch the source takeoff,
 * library pricing or the base estimate).
 *
 * Money rules: an item's amount is qty × unit price unless explicitly
 * overridden; a group's material total is the sum of its items unless an
 * explicit group override is set — the override REPLACES the itemised sum
 * (never both). Labour is an editable group amount. Extra total = Σ group
 * subtotals. Exports (CSV/print) contain ONLY that extra.
 *
 * Items reorder within a group and move between groups by their drag handle
 * (native HTML5 DnD) or the "Move to" select. Removals ask first; the store's
 * document undo also covers every saved change.
 */
import { useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Download, GripVertical, Plus, Printer, RotateCcw, Trash2 } from "lucide-react";
import { Overlay, DialogTitle, ConfirmDialog } from "./dialogs";
import { BTN_GHOST, BTN_ORANGE, INPUT, INPUT_SM, LABEL, MONO } from "./theme";
import { money, fmtQty } from "./pricing";
import { round2 } from "./QuoteDialog";
import { uid } from "./geometry";


// Popups opened with window.open("") share this origin (and its stored
// session), so every value written into them must be HTML-escaped.
const escHtml = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

// ---------- money math (single source of truth for extras) ----------
export const itemAmount = (it) =>
  it.amountOverride != null ? round2(it.amountOverride) : round2((Number(it.quantity) || 0) * (Number(it.unitPrice) || 0));

export const groupTotals = (g) => {
  const itemised = round2((g.items || []).reduce((s, it) => s + itemAmount(it), 0));
  const overridden = g.materialOverride != null;
  const material = overridden ? round2(g.materialOverride) : itemised;
  const labour = round2(g.labour || 0);
  return { itemised, overridden, material, labour, subtotal: round2(material + labour) };
};

export const extraTotals = (x) => {
  const gs = (x?.groups || []).map(groupTotals);
  return {
    material: round2(gs.reduce((s, g) => s + g.material, 0)),
    labour: round2(gs.reduce((s, g) => s + g.labour, 0)),
    total: round2(gs.reduce((s, g) => s + g.subtotal, 0)),
  };
};

export const newExtra = () => ({ id: uid(), name: "", createdAt: Date.now(), updatedAt: Date.now(), groups: [] });

// Quick items whose price destination is this extra ride along as a
// read-only synthetic group, so the extra's totals and exports include them
// while the takeoff item stays the single source of truth. Quantity is the
// measured total (doc items tagged with the palette item's id).
export const withQuickItems = (x, jobItems, measuredItems = []) => {
  const qs = (jobItems || []).filter((i) => i.priceTo === `extra:${x.id}`);
  if (!qs.length) return x;
  const group = {
    id: "__quick", name: "Measured items (assigned from the takeoff)",
    items: qs.map((it) => {
      const qty = (measuredItems || [])
        .filter((m) => m.tagId === it.item_id)
        .reduce((s, m) => s + (Number(m.quantity) || 0) * Math.max(1, Math.round(Number(m.multiplier) || 1)), 0);
      return {
        id: it.item_id || it.tagId || it.name,
        description: it.name,
        quantity: qty,
        unit: it.unit || "ea",
        unitPrice: it.rate || 0,
      };
    }),
  };
  return { ...x, groups: [...(x.groups || []), group] };
};

// ---------- exports (ONLY this extra — never the base estimate or others) ----------
export function extraCsv(x) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const rows = [
    ["Extra", x.name],
    ["Date", new Date(x.updatedAt || x.createdAt || Date.now()).toLocaleDateString()],
    [],
    ["Group", "Description", "Qty", "Unit", "Unit price", "Amount"],
  ];
  for (const g of x.groups || []) {
    const t = groupTotals(g);
    rows.push([g.name, "", "", "", "", ""]);
    for (const it of g.items || []) {
      rows.push(["", it.description, it.quantity, it.unit, Number(it.unitPrice || 0).toFixed(2), itemAmount(it).toFixed(2)]);
    }
    rows.push(["", `Material${t.overridden ? " (override — itemised not counted)" : ""}`, "", "", "", t.material.toFixed(2)]);
    rows.push(["", "Labour", "", "", "", t.labour.toFixed(2)]);
    rows.push(["", "Group subtotal", "", "", "", t.subtotal.toFixed(2)]);
    rows.push([]);
  }
  const t = extraTotals(x);
  rows.push(["Extra material total", "", "", "", "", t.material.toFixed(2)]);
  rows.push(["Extra labour total", "", "", "", "", t.labour.toFixed(2)]);
  rows.push(["EXTRA TOTAL", "", "", "", "", t.total.toFixed(2)]);
  return rows.map((r) => r.map(esc).join(",")).join("\r\n");
}

export const downloadExtraCsv = (x) => {
  const blob = new Blob([extraCsv(x)], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `Extra-${(x.name || "extra").replace(/[^\w-]+/g, "_")}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
};

export const printExtra = (x) => {
  const body = (x.groups || []).map((g) => {
    const t = groupTotals(g);
    const items = (g.items || []).map((it) => `<tr>
      <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0">${escHtml(it.description)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right">${fmtQty(it.quantity)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0">${escHtml(it.unit)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right">${money(it.unitPrice)}</td>
      <td style="padding:4px 6px;border-bottom:1px solid #e2e8f0;text-align:right;font-weight:600">${money(itemAmount(it))}</td>
    </tr>`).join("");
    return `<tr><td colspan="5" style="padding:10px 6px 2px;font-weight:700;text-transform:uppercase;font-size:10px;letter-spacing:.08em;color:#64748b">${escHtml(g.name)}</td></tr>
      ${items || '<tr><td colspan="5" style="padding:4px 6px;color:#94a3b8">No items</td></tr>'}
      <tr><td colspan="4" style="padding:3px 6px;text-align:right;color:#64748b;font-size:11px">Material${t.overridden ? " (override)" : ""} · Labour</td>
        <td style="padding:3px 6px;text-align:right;font-size:11px;color:#64748b">${money(t.material)} · ${money(t.labour)}</td></tr>
      <tr><td colspan="4" style="padding:3px 6px;text-align:right;font-weight:700">Group subtotal</td>
        <td style="padding:3px 6px;text-align:right;font-weight:700">${money(t.subtotal)}</td></tr>`;
  }).join("");
  const t = extraTotals(x);
  const w = window.open("", "_blank", "width=800,height=900");
  if (!w) return;
  w.document.write(`<!doctype html><html><head><title>${escHtml(x.name)}</title>
    <style>body{font-family:ui-sans-serif,system-ui,sans-serif;color:#0f172a;padding:32px}table{width:100%;border-collapse:collapse;font-size:12px}</style></head><body>
    <h1 style="font-size:20px;margin:0">${escHtml(x.name)}</h1>
    <p style="color:#64748b;font-size:12px;margin:4px 0 16px">Extra · ${new Date(x.updatedAt || x.createdAt || Date.now()).toLocaleDateString()}</p>
    <table><thead><tr style="text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:.08em;color:#64748b">
      <th style="padding:4px 6px">Description</th><th style="padding:4px 6px;text-align:right">Qty</th><th style="padding:4px 6px">Unit</th>
      <th style="padding:4px 6px;text-align:right">Unit price</th><th style="padding:4px 6px;text-align:right">Amount</th>
    </tr></thead><tbody>${body}</tbody></table>
    <div style="margin-top:16px;margin-left:auto;width:300px;font-size:13px">
      <div style="display:flex;justify-content:space-between;padding:3px 0"><span>Material total</span><b>${money(t.material)}</b></div>
      <div style="display:flex;justify-content:space-between;padding:3px 0"><span>Labour total</span><b>${money(t.labour)}</b></div>
      <div style="display:flex;justify-content:space-between;padding:6px 0;border-top:2px solid #0f172a;font-size:16px"><span><b>Extra total</b></span><b>${money(t.total)}</b></div>
    </div>
    <script>window.print()</script></body></html>`);
  w.document.close();
};

// ---------- extras list card (lives in the Estimate tab) ----------
export function ExtrasList({ extras, onOpen, onCreate, onDelete, jobItems = [], measuredItems = [] }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="extras-list">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">
          Extras — independent priced scopes, never added to the job total
        </p>
        <button onClick={onCreate} className={BTN_ORANGE} data-testid="extra-create"><Plus className="h-3.5 w-3.5" /> Create extra</button>
      </div>
      {!extras.length && (
        <p className="px-4 py-5 text-center text-xs text-slate-400">No extras yet — create one (e.g. CSI-070) and build it from scratch or from estimate items.</p>
      )}
      <div className="divide-y divide-slate-100">
        {extras.map((x0) => {
          const x = withQuickItems(x0, jobItems, measuredItems);
          const t = extraTotals(x);
          return (
            <div key={x.id} className="flex flex-wrap items-center gap-2 px-4 py-2" data-testid={`extra-row-${x.id}`}>
              <button className="min-w-0 flex-1 text-left" onClick={() => onOpen(x0)} data-testid={`extra-open-${x.id}`}>
                <span className="block truncate text-sm font-semibold text-slate-800 hover:text-[#f97316]">{x.name || "Untitled extra"}</span>
                <span className="block text-[10px] text-slate-400">
                  {(x.groups || []).length} groups · {(x.groups || []).reduce((s, g) => s + (g.items || []).length, 0)} items · material {money(t.material)} · labour {money(t.labour)}
                </span>
              </button>
              <span className={`${MONO} text-sm font-bold text-slate-900`} data-testid={`extra-total-${x.id}`}>{money(t.total)}</span>
              <div className="flex items-center gap-0.5">
                <button aria-label={`Export ${escHtml(x.name)} as CSV`} title="Excel / CSV — this extra only" onClick={() => downloadExtraCsv(x)} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`extra-csv-${x.id}`}><Download className="h-3.5 w-3.5" /></button>
                <button aria-label={`Print ${escHtml(x.name)}`} title="Print — this extra only" onClick={() => printExtra(x)} className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600" data-testid={`extra-print-${x.id}`}><Printer className="h-3.5 w-3.5" /></button>
                <button aria-label={`Delete ${escHtml(x.name)}`} title="Delete" onClick={() => onDelete(x)} className="rounded p-1.5 text-slate-300 hover:bg-red-50 hover:text-red-500" data-testid={`extra-del-${x.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- the extra editor ----------
export default function ExtraEditor({ open, onClose, extra, estimateLines, onSave }) {
  const [x, setX] = useState(null);            // local draft; Save commits
  const [delItem, setDelItem] = useState(null);  // {gid, iid}
  const [delGroup, setDelGroup] = useState(null); // gid
  const [drag, setDrag] = useState(null);        // {gid, iid}
  const [dropHint, setDropHint] = useState(null); // {gid, index}
  const [pickerGroup, setPickerGroup] = useState("");

  useEffect(() => {
    if (!open || !extra) return;
    setX(structuredClone(extra));
    setDelItem(null); setDelGroup(null); setDrag(null); setDropHint(null);
    setPickerGroup(extra.groups?.[0]?.id || "");
  }, [open, extra]);

  const totals = useMemo(() => extraTotals(x), [x]);
  if (!x) return null;

  const patch = (fn) => setX((cur) => { const nx = structuredClone(cur); fn(nx); return nx; });
  const findGroup = (nx, gid) => nx.groups.find((g) => g.id === gid);

  // ---- groups ----
  const addGroup = () => patch((nx) => { const g = { id: uid(), name: `Group ${nx.groups.length + 1}`, materialOverride: null, labour: 0, items: [] }; nx.groups.push(g); setPickerGroup(g.id); });
  const renameGroup = (gid, name) => patch((nx) => { const g = findGroup(nx, gid); if (g) g.name = name; });
  const moveGroup = (gid, dir) => patch((nx) => {
    const i = nx.groups.findIndex((g) => g.id === gid);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= nx.groups.length) return;
    [nx.groups[i], nx.groups[j]] = [nx.groups[j], nx.groups[i]];
  });

  // ---- items ----
  const addManualItem = (gid) => patch((nx) => {
    const g = findGroup(nx, gid);
    if (g) g.items.push({ id: uid(), description: "", quantity: 1, unit: "ea", unitPrice: 0, amountOverride: null });
  });
  const patchItem = (gid, iid, p) => patch((nx) => {
    const it = findGroup(nx, gid)?.items.find((i) => i.id === iid);
    if (!it) return;
    Object.assign(it, p);
    // changing qty/price re-derives the amount unless the amount itself was just set
    if (("quantity" in p || "unitPrice" in p) && !("amountOverride" in p)) it.amountOverride = null;
  });
  /** Move (never copy) an item to a position in any group. */
  const moveItem = (fromGid, iid, toGid, toIndex = null) => patch((nx) => {
    const from = findGroup(nx, fromGid);
    const to = findGroup(nx, toGid);
    if (!from || !to) return;
    const i = from.items.findIndex((it) => it.id === iid);
    if (i < 0) return;
    const [it] = from.items.splice(i, 1);
    let at = toIndex == null ? to.items.length : toIndex;
    if (fromGid === toGid && i < at) at -= 1; // removal shifted the target
    to.items.splice(Math.max(0, Math.min(at, to.items.length)), 0, it);
  });

  const onDropRow = (gid, index) => {
    if (!drag) return;
    moveItem(drag.gid, drag.iid, gid, index);
    setDrag(null); setDropHint(null);
  };
  const onDropGroupEnd = (gid) => {
    if (!drag) return;
    moveItem(drag.gid, drag.iid, gid, null);
    setDrag(null); setDropHint(null);
  };

  const addFromEstimate = (line, gid) => patch((nx) => {
    const g = findGroup(nx, gid);
    if (!g) return;
    g.items.push({   // a snapshot copy — never linked back to the takeoff
      id: uid(), description: line.description, quantity: line.quantity,
      unit: line.unit, unitPrice: line.rate, amountOverride: null,
    });
  });

  const save = () => {
    if (!(x.name || "").trim()) return;
    onSave({ ...x, name: x.name.trim(), updatedAt: Date.now() });
    onClose();
  };

  return (
    <Overlay open={open} onClose={onClose} xl>
      <DialogTitle onClose={onClose}>Extra — items, groups and pricing</DialogTitle>
      <div className="space-y-3" data-testid="extra-editor">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className={LABEL}>Extra name / reference</label>
            <input value={x.name} onChange={(e) => patch((nx) => { nx.name = e.target.value; })} placeholder="CSI-070" className={INPUT} data-testid="extra-name" />
          </div>
          <div className="flex items-end justify-end">
            <button className={BTN_GHOST} onClick={addGroup} data-testid="extra-add-group"><Plus className="mr-1 inline h-3.5 w-3.5" /> Add group</button>
          </div>
        </div>

        {/* groups */}
        <div className="max-h-[46vh] space-y-3 overflow-y-auto pr-1">
          {x.groups.map((g, gi) => {
            const t = groupTotals(g);
            return (
              <div key={g.id} className="rounded-lg border border-slate-200" data-testid={`extra-group-${g.id}`}
                onDragOver={(e) => { e.preventDefault(); if (drag && drag.gid !== g.id) setDropHint({ gid: g.id, index: null }); }}
                onDragLeave={() => setDropHint((h) => (h?.gid === g.id && h.index === null ? null : h))}
                onDrop={(e) => { e.preventDefault(); onDropGroupEnd(g.id); }}>
                <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-slate-50/70 px-3 py-1.5">
                  <input value={g.name} onChange={(e) => renameGroup(g.id, e.target.value)}
                    className="min-w-[140px] flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-[11px] font-bold uppercase tracking-widest text-slate-600 hover:border-slate-200 focus:border-[#f97316] focus:outline-none"
                    aria-label="Group name" data-testid={`group-name-${g.id}`} />
                  <button aria-label="Move group up" title="Move group up" disabled={gi === 0} onClick={() => moveGroup(g.id, -1)} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30" data-testid={`group-up-${g.id}`}><ArrowUp className="h-3 w-3" /></button>
                  <button aria-label="Move group down" title="Move group down" disabled={gi === x.groups.length - 1} onClick={() => moveGroup(g.id, 1)} className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30" data-testid={`group-down-${g.id}`}><ArrowDown className="h-3 w-3" /></button>
                  <button aria-label="Delete group" title="Delete group" onClick={() => setDelGroup(g.id)} className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500" data-testid={`group-del-${g.id}`}><Trash2 className="h-3 w-3" /></button>
                </div>

                {/* items */}
                <div className="divide-y divide-slate-50 overflow-x-auto">
                  {(g.items || []).map((it, ii) => (
                    <div key={it.id}
                      draggable
                      onDragStart={() => setDrag({ gid: g.id, iid: it.id })}
                      onDragEnd={() => { setDrag(null); setDropHint(null); }}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (drag) setDropHint({ gid: g.id, index: ii }); }}
                      onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDropRow(g.id, ii); }}
                      className={`grid min-w-[640px] grid-cols-[18px_minmax(140px,1fr)_60px_52px_76px_84px_88px_26px] items-center gap-1.5 px-2 py-1 ${dropHint?.gid === g.id && dropHint?.index === ii ? "bg-orange-50" : ""} ${drag?.iid === it.id ? "opacity-40" : ""}`}
                      data-testid={`extra-item-${it.id}`}>
                      <span className="cursor-grab text-slate-300 hover:text-slate-500" title="Drag to reorder or move" data-testid={`item-drag-${it.id}`}><GripVertical className="h-4 w-4" /></span>
                      <input value={it.description} onChange={(e) => patchItem(g.id, it.id, { description: e.target.value })} placeholder="Item description"
                        className="min-w-[160px] flex-1 rounded border border-transparent bg-transparent px-1 py-0.5 text-xs text-slate-800 hover:border-slate-200 focus:border-[#f97316] focus:outline-none"
                        aria-label="Item description" data-testid={`item-desc-${it.id}`} />
                      <input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => patchItem(g.id, it.id, { quantity: Number(e.target.value) || 0 })}
                        className={`${INPUT_SM} ${MONO} h-7 w-full text-right`} aria-label="Quantity" data-testid={`item-qty-${it.id}`} />
                      <input value={it.unit} onChange={(e) => patchItem(g.id, it.id, { unit: e.target.value })}
                        className={`${INPUT_SM} ${MONO} h-7 w-full`} aria-label="Unit" data-testid={`item-unit-${it.id}`} />
                      <input type="number" min="0" step="0.01" value={it.unitPrice} onChange={(e) => patchItem(g.id, it.id, { unitPrice: Number(e.target.value) || 0 })}
                        className={`${INPUT_SM} ${MONO} h-7 w-full text-right`} aria-label="Unit price" data-testid={`item-price-${it.id}`} />
                      <input type="number" min="0" step="0.01" key={`${it.id}-${it.amountOverride ?? "auto"}-${it.quantity}-${it.unitPrice}`}
                        defaultValue={itemAmount(it).toFixed(2)}
                        onBlur={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && round2(v) !== itemAmount(it)) patchItem(g.id, it.id, { amountOverride: round2(v) }); }}
                        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                        className={`${INPUT_SM} ${MONO} h-7 w-full text-right font-semibold`} aria-label="Amount" title="Amount (qty × price unless you type your own)" data-testid={`item-amount-${it.id}`} />
                      <select value="" onChange={(e) => { if (e.target.value) moveItem(g.id, it.id, e.target.value, null); }}
                        className={`${INPUT_SM} h-7 w-full text-slate-500`} aria-label="Move to group" data-testid={`item-move-${it.id}`}>
                        <option value="">Move to…</option>
                        {x.groups.filter((og) => og.id !== g.id).map((og) => <option key={og.id} value={og.id}>{og.name}</option>)}
                      </select>
                      <button aria-label="Remove item" title="Remove item" onClick={() => setDelItem({ gid: g.id, iid: it.id })}
                        className="rounded p-1 text-slate-300 hover:bg-red-50 hover:text-red-500" data-testid={`item-del-${it.id}`}><Trash2 className="h-3 w-3" /></button>
                    </div>
                  ))}
                  {!(g.items || []).length && <p className="px-3 py-2 text-[11px] text-slate-400">No items — add one below or drag one in.</p>}
                </div>

                {/* group footer: add item + material/labour/subtotal */}
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-100 px-3 py-1.5">
                  <button onClick={() => addManualItem(g.id)} className="text-[10px] font-semibold uppercase tracking-wider text-[#f97316] hover:underline" data-testid={`group-add-item-${g.id}`}>+ Add item</button>
                  <span className="ml-auto" />
                  <span className={`${MONO} text-[11px] text-slate-500`}>
                    Material{" "}
                    <input type="number" min="0" step="0.01" key={`mat-${g.id}-${t.material}`}
                      defaultValue={t.material.toFixed(2)}
                      onBlur={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && round2(v) !== t.material) patch((nx) => { const gg = findGroup(nx, g.id); if (gg) gg.materialOverride = round2(v); }); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className={`${INPUT_SM} ${MONO} mx-1 inline-block h-6 w-20 text-right`} aria-label="Group material" data-testid={`group-material-${g.id}`} />
                    {t.overridden && (
                      <button onClick={() => patch((nx) => { const gg = findGroup(nx, g.id); if (gg) gg.materialOverride = null; })}
                        title={`Back to the itemised ${money(t.itemised)}`}
                        className="mr-1 inline-flex items-center gap-0.5 rounded bg-amber-100 px-1 py-px text-[9px] font-bold uppercase text-amber-700 hover:bg-amber-200"
                        data-testid={`group-material-reset-${g.id}`}><RotateCcw className="h-2.5 w-2.5" /> override</button>
                    )}
                    Labour{" "}
                    <input type="number" min="0" step="0.01" key={`lab-${g.id}-${t.labour}`}
                      defaultValue={t.labour.toFixed(2)}
                      onBlur={(e) => { const v = Number(e.target.value); if (Number.isFinite(v) && round2(v) !== t.labour) patch((nx) => { const gg = findGroup(nx, g.id); if (gg) gg.labour = round2(v); }); }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
                      className={`${INPUT_SM} ${MONO} mx-1 inline-block h-6 w-20 text-right`} aria-label="Group labour" data-testid={`group-labour-${g.id}`} />
                  </span>
                  <span className={`${MONO} text-xs font-bold text-slate-800`} data-testid={`group-subtotal-${g.id}`}>{money(t.subtotal)}</span>
                </div>
              </div>
            );
          })}
          {!x.groups.length && <p className="py-4 text-center text-xs text-slate-400">No groups yet — add one to start organising this extra.</p>}
        </div>

        {/* pull from the live estimate (snapshot copies) */}
        {x.groups.length > 0 && (estimateLines || []).length > 0 && (
          <div className="rounded-lg border border-slate-200 p-3" data-testid="extra-estimate-picker">
            <div className="mb-1.5 flex flex-wrap items-center gap-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Add from the estimate (copies — the original never changes)</p>
              <select value={pickerGroup} onChange={(e) => setPickerGroup(e.target.value)} className={`${INPUT_SM} h-7`} data-testid="extra-picker-group">
                {x.groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
            </div>
            <div className="max-h-32 overflow-y-auto rounded border border-slate-100">
              {(estimateLines || []).map((l, i) => (
                <div key={i} className="flex items-center gap-2 border-b border-slate-50 px-2 py-1 last:border-0" data-testid={`extra-est-line-${i}`}>
                  <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{l.description}</span>
                  <span className={`${MONO} text-[10px] text-slate-400`}>{fmtQty(l.quantity)} {l.unit} × {money(l.rate)}</span>
                  <button onClick={() => addFromEstimate(l, pickerGroup || x.groups[0].id)}
                    className="rounded p-1 text-slate-300 hover:bg-slate-100 hover:text-[#f97316]" title="Add a copy to the selected group" data-testid={`extra-est-add-${i}`}>
                    <Plus className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* extra totals */}
        <div className="rounded-lg bg-[#0b172a] p-3 text-white" data-testid="extra-totals">
          <div className="flex justify-between text-xs"><span className="text-white/60">Material total</span><span className={MONO} data-testid="extra-material-total">{money(totals.material)}</span></div>
          <div className="flex justify-between text-xs"><span className="text-white/60">Labour total</span><span className={MONO} data-testid="extra-labour-total">{money(totals.labour)}</span></div>
          <div className="mt-1 flex justify-between border-t border-white/10 pt-1 text-sm font-bold"><span>Extra price</span><span className={`${MONO} text-[#fb923c]`} data-testid="extra-grand-total">{money(totals.total)}</span></div>
        </div>

        <div className="flex flex-wrap justify-end gap-2">
          <button className={BTN_GHOST} onClick={() => downloadExtraCsv({ ...x, name: x.name || "extra" })} data-testid="extra-export-csv"><Download className="mr-1 inline h-3.5 w-3.5" /> Excel / CSV</button>
          <button className={BTN_GHOST} onClick={() => printExtra({ ...x, name: x.name || "extra" })} data-testid="extra-print"><Printer className="mr-1 inline h-3.5 w-3.5" /> Print</button>
          <button className={BTN_GHOST} onClick={onClose} data-testid="extra-cancel">Cancel</button>
          <button className={BTN_ORANGE} onClick={save} disabled={!(x.name || "").trim()} data-testid="extra-save">Save extra</button>
        </div>
      </div>

      <ConfirmDialog open={!!delItem} onClose={() => setDelItem(null)}
        title="Remove this item?" body="The item is removed from the group. If you remove one by mistake, cancel without saving."
        confirmLabel="Remove"
        onConfirm={() => { patch((nx) => { const g = findGroup(nx, delItem.gid); if (g) g.items = g.items.filter((i) => i.id !== delItem.iid); }); }} />
      <ConfirmDialog open={!!delGroup} onClose={() => setDelGroup(null)}
        title="Delete this group?" body="The group and its items are removed from the extra. If you delete one by mistake, cancel without saving."
        confirmLabel="Delete group"
        onConfirm={() => { patch((nx) => { nx.groups = nx.groups.filter((g) => g.id !== delGroup); }); }} />
    </Overlay>
  );
}
